import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Star } from 'lucide-react';
import { APP_CONFIG, ROUTES } from '../../config/app.config';

const ELO_GOLD = '#E09900';
const ELO_CTA  = '#FA873D';
const ELO_NAVY = '#1E1E57';
const ELO_DARK = '#0A0A1E';

const SocialLinks = [
  { label: 'FB', href: APP_CONFIG.socialLinks.facebook, title: 'Facebook' },
  { label: 'IG', href: APP_CONFIG.socialLinks.instagram, title: 'Instagram' },
  { label: '𝕏', href: APP_CONFIG.socialLinks.twitter, title: 'X / Twitter' },
  { label: 'in', href: APP_CONFIG.socialLinks.linkedin, title: 'LinkedIn' },
  { label: '▶', href: APP_CONFIG.socialLinks.youtube, title: 'YouTube' },
];

const PublicFooter: React.FC = () => {
  const year = new Date().getFullYear();

  return (
    <footer style={{ background: ELO_DARK, color: '#9ab1e8' }}>
      {/* CTA Band */}
      <div style={{
        background: `linear-gradient(135deg, ${ELO_CTA}, ${ELO_GOLD})`,
        padding: '3.5rem 1.5rem', textAlign: 'center',
      }}>
        <h2 style={{ color: 'white', fontSize: '1.75rem', fontWeight: 900, marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '-0.5px' }}>
          Ready for a Free Roof Inspection?
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.88)', marginBottom: '1.5rem', maxWidth: '480px', margin: '0 auto 1.5rem' }}>
          No pressure. No obligation. Just an honest assessment from a licensed professional.
        </p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to={ROUTES.QUOTE} style={{
            background: 'white', color: ELO_NAVY, padding: '0.75rem 2rem',
            borderRadius: '100px', fontWeight: 800, textDecoration: 'none',
            fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.04em',
            fontFamily: "'Roboto', sans-serif",
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ''; (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
          >
            Get Free Quote
          </Link>
          <a href={`tel:${APP_CONFIG.phoneRaw}`} style={{
            border: '2px solid rgba(255,255,255,0.55)', color: 'white',
            padding: '0.75rem 2rem', borderRadius: '100px', fontWeight: 700,
            textDecoration: 'none', fontSize: '0.9rem', textTransform: 'uppercase',
            letterSpacing: '0.04em', fontFamily: "'Roboto', sans-serif",
          }}>
            {APP_CONFIG.phone}
          </a>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-xl" style={{ padding: '4rem 1.5rem 2rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '3rem', marginBottom: '3rem' }}>
          {/* Brand */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1.25rem' }}>
              <div style={{ background: `rgba(224,153,0,0.15)`, border: `1px solid rgba(224,153,0,0.3)`, borderRadius: '0.625rem', padding: '4px 6px' }}>
                <img
                  src="/cates-logo.png"
                  alt="Cates Roofing"
                  style={{ height: '44px', width: 'auto', objectFit: 'contain', display: 'block' }}
                  onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ color: 'white', fontWeight: 900, fontSize: '1.1rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>CATES</div>
                <div style={{ color: ELO_GOLD, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Roofing &amp; Contracting</div>
              </div>
            </div>
            <p style={{ fontSize: '0.875rem', lineHeight: 1.7, marginBottom: '1.25rem', maxWidth: '260px' }}>
              {APP_CONFIG.description}
            </p>
            <div style={{ display: 'flex', gap: '0.625rem' }}>
              {SocialLinks.map(s => (
                <a key={s.href} href={s.href} title={s.title} target="_blank" rel="noopener noreferrer" style={{
                  width: '2.25rem', height: '2.25rem', borderRadius: '0.5rem',
                  border: `1px solid rgba(30,30,87,0.5)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#9ab1e8', transition: 'all 0.2s',
                  fontSize: '0.7rem', fontWeight: 700, textDecoration: 'none',
                }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = ELO_GOLD; (e.currentTarget as HTMLElement).style.color = ELO_GOLD; (e.currentTarget as HTMLElement).style.background = 'rgba(224,153,0,0.1)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(30,30,87,0.5)'; (e.currentTarget as HTMLElement).style.color = '#9ab1e8'; (e.currentTarget as HTMLElement).style.background = ''; }}>
                  {s.label}
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 style={{ color: 'white', fontWeight: 800, fontSize: '0.85rem', marginBottom: '1.25rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Services</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              {[
                ['Roof Cleaning', '/services/roof-cleaning'],
                ['Roof Repair', '/services/roof-repair'],
                ['Roof Replacement', '/services/roof-replacement'],
                ['Commercial Roofing', '/services/commercial-roofing'],
                ['Emergency Service', '/services/emergency-roofing'],
                ['Roof Inspection', '/services/roof-inspection'],
                ['Gutter Cleaning', '/services/gutter-cleaning'],
              ].map(([label, to]) => (
                <li key={to}>
                  <Link to={to} style={{ fontSize: '0.875rem', color: '#9ab1e8', textDecoration: 'none', transition: 'color 0.2s' }}
                    onMouseEnter={e => (e.currentTarget.style.color = ELO_GOLD)}
                    onMouseLeave={e => (e.currentTarget.style.color = '#9ab1e8')}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 style={{ color: 'white', fontWeight: 800, fontSize: '0.85rem', marginBottom: '1.25rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Company</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              {[
                ['About Us', ROUTES.ABOUT],
                ['Gallery', ROUTES.GALLERY],
                ['Blog', ROUTES.BLOG],
                ['Testimonials', ROUTES.TESTIMONIALS],
                ['Service Areas', ROUTES.SERVICE_AREAS],
                ['Financing', ROUTES.FINANCING],
                ['Careers', ROUTES.CAREERS],
                ['FAQ', ROUTES.FAQ],
              ].map(([label, to]) => (
                <li key={to}>
                  <Link to={to} style={{ fontSize: '0.875rem', color: '#9ab1e8', textDecoration: 'none', transition: 'color 0.2s' }}
                    onMouseEnter={e => (e.currentTarget.style.color = ELO_GOLD)}
                    onMouseLeave={e => (e.currentTarget.style.color = '#9ab1e8')}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 style={{ color: 'white', fontWeight: 800, fontSize: '0.85rem', marginBottom: '1.25rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Contact</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <a href={`tel:${APP_CONFIG.phoneRaw}`} style={{ display: 'flex', gap: '0.75rem', color: '#9ab1e8', textDecoration: 'none', fontSize: '0.875rem' }}>
                <Phone size={16} style={{ color: ELO_GOLD, flexShrink: 0, marginTop: '2px' }} />
                <span>{APP_CONFIG.phone}</span>
              </a>
              <a href={`mailto:${APP_CONFIG.email}`} style={{ display: 'flex', gap: '0.75rem', color: '#9ab1e8', textDecoration: 'none', fontSize: '0.875rem' }}>
                <Mail size={16} style={{ color: ELO_GOLD, flexShrink: 0, marginTop: '2px' }} />
                <span>{APP_CONFIG.email}</span>
              </a>
              <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.875rem' }}>
                <MapPin size={16} style={{ color: ELO_GOLD, flexShrink: 0, marginTop: '2px' }} />
                <span>{APP_CONFIG.address}<br />{APP_CONFIG.city}, {APP_CONFIG.state} {APP_CONFIG.zip}</span>
              </div>
            </div>
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ color: 'white', fontWeight: 700, fontSize: '0.8rem', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Business Hours</h4>
              <div style={{ fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <span>Mon–Fri: {APP_CONFIG.businessHours.weekdays}</span>
                <span>Saturday: {APP_CONFIG.businessHours.saturday}</span>
                <span style={{ color: ELO_CTA }}>Sunday: {APP_CONFIG.businessHours.sunday}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div style={{ borderTop: `1px solid rgba(30,30,87,0.6)`, paddingTop: '2rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', alignItems: 'center', justifyContent: 'center' }}>
            {[
              { icon: '⭐', label: '4.9/5 Rating', sub: '500+ Reviews' },
              { icon: '🏆', label: 'GAF Master Elite', sub: 'Certified Contractor' },
              { icon: '🛡️', label: '10-Year Warranty', sub: 'On All Work' },
              { icon: '💳', label: '0% Financing', sub: '18 Months' },
              { icon: '🚨', label: '24/7 Emergency', sub: '2-Hour Response' },
            ].map(badge => (
              <div key={badge.label} style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                background: ELO_NAVY, border: `1px solid rgba(43,43,125,0.6)`,
                borderRadius: '0.75rem', padding: '0.625rem 1rem',
                transition: 'border-color 0.2s ease',
              }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = ELO_GOLD; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(43,43,125,0.6)'; }}
              >
                <span style={{ fontSize: '1.25rem' }}>{badge.icon}</span>
                <div>
                  <div style={{ color: 'white', fontSize: '0.8rem', fontWeight: 700 }}>{badge.label}</div>
                  <div style={{ fontSize: '0.7rem' }}>{badge.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Star Rating */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', marginBottom: '0.25rem' }}>
            {[1,2,3,4,5].map(i => <Star key={i} size={18} fill={ELO_GOLD} stroke="none" />)}
          </div>
          <p style={{ fontSize: '0.8rem' }}>4.9 average rating from 500+ verified reviews</p>
        </div>

        {/* Bottom Bar */}
        <div style={{ borderTop: `1px solid rgba(30,30,87,0.6)`, paddingTop: '1.5rem', display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem' }}>
          <span>© {year} {APP_CONFIG.name}. All rights reserved.</span>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            {APP_CONFIG.licenses.map(l => (
              <span key={l} style={{ color: '#6b7280' }}>{l}</span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Link to={ROUTES.PRIVACY} style={{ color: '#6b7280', textDecoration: 'none' }}
              onMouseEnter={e => (e.currentTarget.style.color = ELO_GOLD)}
              onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}>Privacy</Link>
            <Link to={ROUTES.TERMS} style={{ color: '#6b7280', textDecoration: 'none' }}
              onMouseEnter={e => (e.currentTarget.style.color = ELO_GOLD)}
              onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}>Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default PublicFooter;
